import React from 'react'
import { createPortal } from 'react-dom'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { useFeature } from '@core/Utils/hooks/useFeature'
import { ShareWithExternal } from './Components/ShareWithExternal'
import { shareManagerEmitter } from './helpers/emitter'
import { ShareOptions } from './Components/ShareOptions'
import type { ShareBetData } from '../types'
import { isSharedPlacedBetPending } from './helpers'
import { isBetLoopShareData } from './helpers/typeGuards'

export const ShareBetOutgoing: React.FC = () => {
  const hasAlreadyOpenShareRef = React.useRef(false)
  
  const [share, setShare] = React.useState<ShareBetData | null>(null)
  const [isShareOpen, setIsShareOpen] = React.useState(false)

  hasAlreadyOpenShareRef.current = !!share

  React.useEffect(() => {
    shareManagerEmitter.on('openShareOutgoing', share => {
      if (hasAlreadyOpenShareRef.current) return
      setShare(share)
      setIsShareOpen(true)
    })
  }, [])

  const handleDismiss = () => setIsShareOpen(false)
  const resetShare = () => setShare(null)

  return createPortal(
    <Tray
      isOpen={isShareOpen}
      headerTitle='Share my Bet'
      onDismiss={handleDismiss}
      trayStyle={{ maxWidth: '48rem' }}
    >
      {share && (
        <ShareTrayContent data={share} resetShare={resetShare} dismissTray={handleDismiss} />
      )}
    </Tray>,
    document.body
  )
}

// ===============
// Local Component
// ===============

const ShareTrayContent = React.memo<{
  data: ShareBetData
  resetShare(): void
  dismissTray(): void
}>(
  ({ data, resetShare, dismissTray }) => {
    const isBetLoopFeatureActive = useFeature('content-hub-follow-me')
    const [isExternalShare, setIsExternalShare] = React.useState(false)

    React.useEffect(() => resetShare, [resetShare])

    const isPending = isSharedPlacedBetPending(data)
    const isBetLoopShare = isBetLoopShareData(data)

    if (isBetLoopFeatureActive && !isExternalShare && isPending && !isBetLoopShare) {
      return (
        <ShareOptions
          handleShareWithExternalClick={() => setIsExternalShare(true)}
          data={data}
          dismissTray={dismissTray}
        />
      )
    }

    return data ? <ShareWithExternal data={data} /> : null
  },
  () => true
)

ShareTrayContent.displayName = 'ShareTrayContent'
